import React, { useCallback, type ReactElement } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Flex } from '@atlaskit/primitives/compiled';
import { ExpandableMenuItemContent, ExpandableMenuItemTrigger } from '@atlaskit/navigation-system';
import { SECTION_ITEM_PLANS } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_PLANS } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import {
	L1_MENU_ID,
	type Nav4DragAndDropMenuItemProps,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { Nav4ExpandableMenuItem } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { getTestId } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { useSectionItemAnalytics } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/use-section-item-analytics/index.tsx';
import { useConfigurableMessageAndIconMap } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import { PaidFeatureAwarenessRenderer } from '@atlassian/jira-paid-feature-awareness-renderer/src/ui/PaidFeatureAwarenessRendererComponent.tsx';
import { PaidFeatureAwarenessPremiumLozenge } from '@atlassian/jira-paid-feature-awareness-renderer/src/ui/lozenges/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { componentWithFG } from '@atlassian/jira-feature-gate-component';
import { Nav4PlansFooter } from '../common/footer/Nav4PlansFooter.tsx';
import { PlansList } from '../common/plans-list/PlansList.tsx';
import { PlansListRefactored } from '../common/plans-list/PlansListRefactored.tsx';
import type {
	Nav4PlansForLanding$key,
	Nav4PlansForLanding$data,
} from './__generated__/Nav4PlansForLanding.graphql';

type Nav4PlansForLandingProps = Nav4DragAndDropMenuItemProps & {
	actionsOnHover: ReactElement;
	isExpanded: boolean;
	queryRef: Nav4PlansForLanding$key;
};

export function Nav4PlansForLanding(props: Nav4PlansForLandingProps): React.JSX.Element {
	const { queryRef } = props;
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.PLANS);
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();

	const data = useFragment(
		graphql`
			fragment Nav4PlansForLanding on Query {
				...PlansList
				...PlansListRefactored
			}
		`,
		queryRef,
	);

	const menuId = MENU_ID_PLANS;

	const handleExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				sendSectionItemAnalyticsEvent({
					menuId: L1_MENU_ID.PLANS,
					sectionItem: SECTION_ITEM_PLANS,
				});
			}
		},
		[sendSectionItemAnalyticsEvent],
	);

	return (
		<Nav4PlansForLandingInner
			{...props}
			formattedMessage={formattedMessage}
			handleExpansionToggle={handleExpansionToggle}
			icon={icon}
			menuId={menuId}
			queryRef={data}
		/>
	);
}

const Nav4PlansForLandingInner = componentWithFG(
	'navx-global-side-nav',
	Nav4PlansForLandingInnerGSN,
	Nav4PlansForLandingInnerNav4,
);

type InnerProps = Omit<Nav4PlansForLandingProps, 'queryRef'> & {
	formattedMessage: string;
	handleExpansionToggle: (hasExpanded: boolean) => void;
	icon: JSX.Element;
	menuId: string;
	queryRef: Nav4PlansForLanding$data;
};

function Nav4PlansForLandingInnerGSN(props: InnerProps) {
	const {
		actionsOnHover,
		draggableRef,
		dropIndicator,
		formattedMessage,
		handleExpansionToggle,
		hasDragIndicator,
		icon,
		isDragging,
		isExpanded,
		menuId,
		onExpandFlyoutToggle,
		queryRef,
		visualContentRef,
	} = props;

	const handleExpansionToggle2 = useCallback(
		(hasExpanded: boolean) => {
			handleExpansionToggle(hasExpanded);
			onExpandFlyoutToggle?.(hasExpanded);
		},
		[onExpandFlyoutToggle, handleExpansionToggle],
	);

	return (
		<Nav4ExpandableMenuItem
			dropTargetRef={visualContentRef}
			dropIndicator={dropIndicator}
			isDragging={isDragging}
			isExpanded={isExpanded}
			menuId={menuId}
			onExpansionToggle={handleExpansionToggle2}
		>
			<ExpandableMenuItemTrigger
				actionsOnHover={actionsOnHover}
				elemBefore={icon}
				hasDragIndicator={hasDragIndicator}
				interactionName="nav4-sidebar-plans-landing"
				isDragging={isDragging}
				ref={draggableRef}
				testId={getTestId(menuId)}
			>
				<Flex gap="space.100" alignItems="center">
					{formattedMessage}
					<PaidFeatureAwarenessRenderer
						component={<PaidFeatureAwarenessPremiumLozenge />}
						placement="navigation.plans"
					/>
				</Flex>
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				{/* this component uses the fragment from the ancestor query */}
				{expVal('navx-2365-add_dnd_to_jira_l2_menu_items', 'isEnabled', false) ? (
					<PlansListRefactored fragmentRef={queryRef} />
				) : (
					<PlansList fragmentRef={queryRef} />
				)}
				<Nav4PlansFooter />
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}

function Nav4PlansForLandingInnerNav4(props: InnerProps) {
	const {
		actionsOnHover,
		formattedMessage,
		handleExpansionToggle,
		icon,
		isExpanded,
		menuId,
		queryRef,
	} = props;

	return (
		<Nav4ExpandableMenuItem
			isExpanded={isExpanded}
			menuId={menuId}
			onExpansionToggle={handleExpansionToggle}
		>
			<ExpandableMenuItemTrigger
				interactionName="nav4-sidebar-plans-landing"
				actionsOnHover={actionsOnHover}
				elemBefore={icon}
				testId={getTestId(menuId)}
			>
				<Flex gap="space.100" alignItems="center">
					{formattedMessage}
					<PaidFeatureAwarenessRenderer
						component={<PaidFeatureAwarenessPremiumLozenge />}
						placement="navigation.plans"
					/>
				</Flex>
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				{/* this component uses the fragment from the ancestor query */}
				{expVal('navx-2365-add_dnd_to_jira_l2_menu_items', 'isEnabled', false) ? (
					<PlansListRefactored fragmentRef={queryRef} />
				) : (
					<PlansList fragmentRef={queryRef} />
				)}
				<Nav4PlansFooter />
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}
